import { useState, useCallback, useEffect } from "react";
import { useAuthStore } from "@/state/authStore";
import type { PlanName } from "@/lib/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Uma chave de dentro de um parâmetro-objeto (`tools`, `info`).
 *
 * Existe porque «Flags: a, b, c, d…» numa linha só não é documentação: quem
 * emite o link precisa saber, por chave, o que o convidado deixa de ver e se
 * a chave é uma **garantia** ou só um botão escondido.
 */
interface ParamField {
  name: string;
  type: string;
  /** O valor quando a chave não é enviada. */
  default: string;
  /**
   * Onde a chave é aplicada — e é por isso que esta coluna existe.
   *
   * «tela e rota»: além de sumir da tela, o endpoint correspondente responde
   * 403 a quem o chamar com este token. «só tela»: o controle não é desenhado,
   * mas a rota por trás não consulta a chave. «só rota»: não há controle na
   * tela hoje; a chave governa apenas o endpoint. «tela e payload»: não há
   * endpoint a barrar — o dado é calculado no servidor e simplesmente não sai
   * no bootstrap do link, o que guarda tanto quanto um 403.
   *
   * Ausente onde a pergunta não se aplica: os campos de `info` são texto a
   * mostrar, não permissão — ali a coluna (e a legenda) somem.
   */
  scope?: "tela e rota" | "só tela" | "só rota" | "tela e payload";
  description: string;
  /** O que muda neste endpoint, quando é diferente do caso geral. */
  note?: string;
  /**
   * O menor plano em que a chave existe (set/2026). Com `plan` no link, a
   * chave acima do plano aparece travada com o selo e a rota responde 403.
   * A fonte viva é GET /integration/v1/plans; esta coluna é a cópia lida.
   */
  plan?: PlanName;
}

interface Param {
  name: string;
  type: string;
  required: boolean;
  location: "body" | "query" | "header" | "path";
  default?: string;
  description: string;
  /** As chaves de um parâmetro-objeto, detalhadas uma a uma. */
  fields?: ParamField[];
}

interface Endpoint {
  id: string;
  method: "GET" | "POST" | "DELETE";
  path: string;
  summary: string;
  description: string;
  params: Param[];
  requestExample?: string;
  responseExample: string;
  /** Resposta binária: o curl de exemplo grava neste arquivo (`-o`) em vez de despejar no terminal. */
  responseFile?: string;
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const BASE = "/api/v1";
const INTEGRATION_BASE = "/api/integration/v1";

/**
 * As chaves de `tools`, na ordem em que o backend as declara
 * (`PublicViewToolsIn`). Uma lista só, usada pelos dois endpoints: a do link
 * unitário e a do par são o **mesmo** modelo, e mantê-las em duas listas seria
 * garantir que uma envelhecesse.
 *
 * O que muda entre os dois é a nota de contexto, que `comNotas` acrescenta.
 */
const TOOLS_FIELDS: ParamField[] = [
  {
    name: "channel_selector",
    plan: "starter",
    type: "boolean",
    default: "true",
    scope: "só tela",
    description:
      "A seção «Separação de cor» do Painel Técnico, à direita: a lista de tintas do arquivo, com o que ligar e desligar na visualização. Desligada, some a seção — e com ela o filtro de cobertura, que mora dentro dela. O painel continua pelo que sobra nele: num PDF, o «Código de barras e QR», se barcode_auto deixar (ou o selo do plano que o libera); no conjunto de separações TIFF, a «Retícula», se screening deixar, e o seletor LEN | VIEW quando a peça tem VIEW. Sem nenhuma dessas seções, o painel não é montado.",
  },
  {
    name: "eyedropper",
    plan: "starter",
    type: "boolean",
    default: "true",
    scope: "tela e rota",
    description:
      "O «Conta Gotas»: um clique na arte devolve a cobertura de cada tinta naquele ponto e o TAC somado. Só existe onde há tinta a medir (PDF e conjunto de separações); em imagem, o backend já não a oferece.",
  },
  {
    name: "coverage_filter",
    plan: "starter",
    type: "boolean",
    default: "true",
    scope: "tela e rota",
    description:
      "O filtro de cobertura do Painel Técnico: pinta sobre a arte as áreas em que a soma de tinta fica abaixo do limite escolhido — é como se acha tinta fraca demais para imprimir.",
  },
  {
    name: "barcode",
    plan: "starter",
    type: "boolean",
    default: "true",
    scope: "tela e rota",
    description:
      "O leitor sob demanda «Ler Código»: o convidado arrasta uma área sobre a arte e o servidor lê o código que houver ali. Desligada, o botão e o atalho somem e a rota do leitor responde 403. Desde 13/09 governa só o leitor; o painel com a varredura da página é o barcode_auto.",
  },
  {
    name: "barcode_auto",
    plan: "pro",
    type: "boolean",
    default: "true",
    scope: "tela e payload",
    description:
      "O painel «Código de barras e QR», com o resultado da varredura automática da página: códigos agrupados, quantas posições cada um ocupa e o HRI conferido por OCR. A varredura roda sempre no servidor, para todo mundo; a chave decide se o convidado recebe o resultado — desligada, o payload do link não entrega os códigos. Ausente num link anterior a 13/09, herda o barcode.",
  },
  {
    name: "braille",
    plan: "pro",
    type: "boolean",
    default: "true",
    scope: "tela e rota",
    description: "O «Ler Braile»: mede os pontos de braile na área selecionada e devolve o texto e o passo.",
  },
  {
    name: "ocr",
    plan: "pro",
    type: "boolean",
    default: "true",
    scope: "tela e rota",
    description:
      "O «Ler Texto»: OCR na área que o convidado selecionar com o mouse. A leitura sai com o idioma identificado linha a linha (bloco `language`: português, inglês, espanhol, francês, italiano, alemão ou árabe; pt-BR é o padrão quando nada decide) e com a conferência ortográfica por palavra de cada linha no idioma dela (bloco `spellcheck`, um estado por idioma em `languages`): as palavras que o dicionário não reconhece aparecem grifadas no balão, com a correção ao lado, e «Sem erros identificados» quando não há nenhuma. O balão tem um seletor de idioma que abre no identificado; trocá-lo relê a região com aquele idioma (campo `language` do POST /tools/ocr), e no árabe troca também o pacote do OCR.",
  },
  {
    name: "table_ocr",
    plan: "enterprise",
    type: "boolean",
    default: "true",
    scope: "só rota",
    description:
      "A leitura de tabela por OCR (POST /tools/table-ocr). O viewer não oferece hoje um botão para ela, então ligar ou desligar não muda o que o convidado vê: a chave governa só quem chamar a rota com este token.",
  },
  {
    name: "text_mode",
    plan: "enterprise",
    type: "boolean",
    default: "false",
    scope: "tela e rota",
    description:
      "O «Modo Texto»: destaca, sobre a arte, o texto que o Google Vision identificou no render, com busca e seleção. Nasce desligada porque o motor é uma API externa paga — um link público precisa optar por ela.",
  },
  {
    name: "notes",
    plan: "starter",
    type: "boolean",
    default: "true",
    scope: "só tela",
    description:
      "Ver os comentários — separado de escrevê-los. Desligada, o botão «Comentários» não aparece e a camada de notas não é desenhada: é como se emite um link que mostra a arte sem mostrar a conversa interna sobre ela.",
  },
  {
    name: "notes_create",
    plan: "starter",
    type: "boolean",
    default: "true",
    scope: "tela e rota",
    description:
      "Escrever comentários: os botões «Ponto» e «Seleção», e responder. Desligada, o convidado lê e não escreve — a rota de criação responde 403. A autoria vem dos cabeçalhos X-LinkFlow-Requester-*.",
  },
  {
    name: "notes_report",
    plan: "pro",
    type: "boolean",
    default: "true",
    scope: "tela e rota",
    description:
      "O botão «Exportar relatório» do painel de comentários: um PDF com a ficha do link, a arte de cada página com as anotações numeradas no lugar em que foram feitas e, embaixo de cada arte, a lista delas com status, autor e comentário. Desligada, o botão não aparece e a rota responde 403. A arte vai rasterizada no preview — o relatório não entrega o arquivo original, que é a chave download.",
  },
  {
    name: "alignment_auto",
    plan: "pro",
    type: "boolean",
    default: "true",
    scope: "só tela",
    description:
      "O encaixe automático do par. Ligada, a primeira abertura alinha sozinha e a barra oferece «Auto» e «Por objeto». Desligada, o par abre como está e sobra o encaixe «Por referência» (o convidado clica no mesmo ponto dos dois lados), que roda no navegador.",
  },
  {
    name: "differences",
    plan: "pro",
    type: "boolean",
    default: "true",
    scope: "só tela",
    description:
      "O painel «Diferenças encontradas», à direita do par: o botão «Identificar diferenças», a granulometria e a lista de regiões com recorte. Desligada, o painel não é montado — a arte, os modos de visualização e a máscara do Delta continuam, porque elas são a comparação; o que sai é a enumeração dos achados.",
  },
  {
    name: "document_conference",
    plan: "pro",
    type: "boolean",
    default: "true",
    scope: "tela e rota",
    description:
      "A conferência de dizeres no par de um DOCUMENTO com uma ARTE de embalagem (a especificação × o PDF gráfico): o documento abre à esquerda e a arte à direita, o convidado seleciona um trecho do documento e a tela diz se ele está na arte — confere, diverge (com a palavra que difere e o recorte da arte como prova) ou ausente —, pelo texto que o Google Vision identificou na arte. É ela que permite emitir esse par: no plano Starter, ou com false, POST /public-compares responde 422 dizendo qual arquivo é o documento e qual é a arte. A ordem de path_a e path_b não importa. Sem a leitura do Vision na arte, a tela avisa e a conferência espera por ela; o link nunca a dispara.",
  },
  {
    name: "screening",
    plan: "starter",
    type: "boolean",
    default: "true",
    scope: "só tela",
    description:
      "Só no conjunto de separações TIFF. A seção «Retícula» do Painel Técnico: lineatura, ângulo e ponto que cada tinta declara, e o detalhe da tinta. Desligada, a seção não aparece. No TIFF não há plano Pro: vale em todo plano.",
  },
  {
    name: "screen_measure",
    plan: "starter",
    type: "boolean",
    default: "true",
    scope: "tela e rota",
    description:
      "Só no conjunto de separações TIFF. «Medir Retícula»: o convidado arrasta uma área e o servidor mede lineatura e ângulo de cada tinta ali, conferindo com o que o arquivo declara. Desligada, o botão não aparece e POST /api/public-views/{token}/tools/screen-measure responde 403. Vale em todo plano.",
  },
  {
    name: "download",
    plan: "starter",
    type: "boolean",
    default: "false",
    scope: "tela e rota",
    description:
      "Baixar o arquivo original pelo link: o botão no painel «Informações» e a rota GET /api/public-views/{token}/download. É a única chave que nasce desligada — as outras nascem ligadas para que um link emitido antes delas continue como era, e «como era» aqui é sem entregar o arquivo.",
  },
  {
    name: "dictionary",
    plan: "starter",
    type: "boolean",
    default: "false",
    scope: "tela e rota",
    description:
      "O botão «aceitar» nos erros de ortografia do Ler Texto e do Modo Texto: a palavra passa a valer no dicionário da empresa do link (X-LinkFlow-Organization-Id), nunca no global, e deixa de ser acusada nos arquivos dela. Nasce desligada — ligue só para quem da empresa pode aceitar. Sem empresa no link, o botão não aparece e POST /api/public-views/{token}/dictionary responde 403. Remover palavra é só pelo gerenciador.",
  },
];

/** A mesma lista, com a nota de contexto de cada endpoint. */
function comNotas(notas: Record<string, string>): ParamField[] {
  return TOOLS_FIELDS.map((campo) => (notas[campo.name] ? { ...campo, note: notas[campo.name] } : campo));
}

const SEM_EFEITO_NO_UNITARIO = "Só no par: num link de um arquivo só não há o que alinhar nem o que comparar.";

const TOOLS_FIELDS_VIEW = comNotas({
  alignment_auto: SEM_EFEITO_NO_UNITARIO,
  differences: SEM_EFEITO_NO_UNITARIO,
  document_conference: SEM_EFEITO_NO_UNITARIO,
});

const TOOLS_FIELDS_COMPARE = comNotas({
  channel_selector: "Sem efeito no par: a tela de comparação não tem Painel Técnico.",
  coverage_filter: "Sem efeito no par: o controle do filtro mora no Painel Técnico, que a comparação não tem.",
  screening: "Sem efeito no par: a comparação não abre o conjunto TIFF.",
  screen_measure: "Sem efeito no par: a comparação não abre o conjunto TIFF.",
  text_mode: "No par o botão aparece, mas o destaque não é desenhado: o Modo Texto é do link unitário.",
  download: "No par a rota aceita ?side=a|b, mas a tela não tem o painel «Informações» onde o botão mora.",
  eyedropper: "No par, o Conta Gotas lê os DOIS arquivos no mesmo ponto físico da arte e mostra as leituras lado a lado.",
  notes: "No par, vale para os dois lados: as notas são do arquivo, e a tela pede o lado.",
  notes_create: "No par, vale para os dois lados.",
});

/**
 * Os campos de `info` — a ficha do trabalho que o painel «Informações» mostra.
 */
const INFO_FIELDS: ParamField[] = [
  {
    name: "description",
    type: "string (≤ 512)",
    default: "null",
    description: "Descrição do trabalho — a primeira linha do painel. Ex.: «WPC Caramelo Salgado 900g — R02».",
  },
  {
    name: "client_code",
    type: "string (≤ 128)",
    default: "null",
    description: "Código do cliente, como ele aparece no seu sistema.",
  },
  {
    name: "reference_code",
    type: "string (≤ 128)",
    default: "null",
    description: "Código de referência da peça — a revisão, o item, o que identificar este trabalho.",
  },
  {
    name: "observations_html",
    type: "string (≤ 65536)",
    default: "null",
    description:
      "Observações em HTML de editor: parágrafos, ênfase, listas, títulos, links e tabela simples. Sanitizado na entrada — script, estilo, imagem e atributos são removidos, e o que a tela renderiza é o que saiu do saneamento, nunca o que entrou.",
  },
];

const ENDPOINTS: Endpoint[] = [
  {
    id: "create-api-key",
    method: "POST",
    path: `${BASE}/api-keys`,
    summary: "Criar API key",
    description:
      "Cria uma nova API key vinculada ao usuário autenticado. O segredo completo (lfk_...) é retornado apenas nesta resposta — guarde-o imediatamente.",
    params: [
      {
        name: "name",
        type: "string",
        required: true,
        location: "body",
        description: "Nome descritivo para identificar a chave (ex: 'Integração ERP').",
      },
      {
        name: "expires_at",
        type: "datetime (ISO 8601)",
        required: false,
        location: "body",
        description: "Data de expiração opcional. Se omitida, a chave não expira.",
      },
    ],
    requestExample: JSON.stringify({ name: "Integração ERP", expires_at: null }, null, 2),
    responseExample: JSON.stringify(
      {
        api_key: "lfk_EXEMPLO_SECRETO_NAOGUARDAR",
        key: {
          id: "b8f2e1d3-...",
          user_id: "a1b2c3d4-...",
          name: "Integração ERP",
          key_prefix: "a3f9c12b4e7d",
          last_used_at: null,
          expires_at: null,
          revoked_at: null,
          created_at: "2026-05-12T17:00:00Z",
        },
      },
      null,
      2,
    ),
  },
  {
    id: "list-api-keys",
    method: "GET",
    path: `${BASE}/api-keys`,
    summary: "Listar API keys",
    description: "Lista todas as API keys do usuário autenticado.",
    params: [],
    responseExample: JSON.stringify(
      [
        {
          id: "b8f2e1d3-...",
          user_id: "a1b2c3d4-...",
          name: "Integração ERP",
          key_prefix: "a3f9c12b4e7d",
          last_used_at: "2026-05-12T16:00:00Z",
          expires_at: null,
          revoked_at: null,
          created_at: "2026-05-12T17:00:00Z",
        },
      ],
      null,
      2,
    ),
  },
  {
    id: "revoke-api-key",
    method: "DELETE",
    path: `${BASE}/api-keys/{key_id}`,
    summary: "Revogar API key",
    description: "Revoga imediatamente uma API key do usuário autenticado.",
    params: [
      {
        name: "key_id",
        type: "UUID",
        required: true,
        location: "path",
        description: "ID da chave a revogar (campo id da listagem).",
      },
    ],
    responseExample: JSON.stringify(
      {
        id: "b8f2e1d3-...",
        revoked_at: "2026-05-12T18:00:00Z",
      },
      null,
      2,
    ),
  },
  {
    id: "file-exists",
    method: "GET",
    path: `${INTEGRATION_BASE}/files/exists`,
    summary: "Verificar arquivo",
    description:
      "Diz se o viewer guarda os bytes deste path: consulte antes de abrir. exists=true quando o path foi registrado pelo watcher/upload e tem arquivo vivo (inclusive com erro de render). exists=false sem mais nada quando o path nunca chegou; exists=false com expired=true e expired_at quando o arquivo expirou no viewer — o registro, as notas e os links ficaram, mas pirâmide e original saíram para liberar disco. Nos dois casos de false, reenvie o arquivo pelo mesmo path: o mesmo registro revive (mesmo slug, mesmas notas) e o render é refeito sozinho. Arquivo com erro de render continua exists=true de propósito: o original está guardado, e reenviar o mesmo conteúdo não cura um erro de render — o reenvio seria ingerido e apagado da caixa de entrada sem mudar nada, num laço de reenvio a cada abertura. Para tentar de novo, use POST renders.",
    params: [
      {
        name: "path",
        type: "string",
        required: true,
        location: "query",
        description: "Caminho lógico do arquivo (ex: 'approvals/123456/arquivo.pdf').",
      },
    ],
    responseExample: JSON.stringify(
      {
        path: "approvals/123456/WPC_Caramelo_Salgado_900g_20041254_R02_Editavel.pdf",
        exists: true,
      },
      null,
      2,
    ),
  },
  {
    id: "preprepare-status",
    method: "GET",
    path: `${INTEGRATION_BASE}/preprepare/status`,
    summary: "Status do preprepare",
    description:
      "Consulta o processamento inicial disparado pelo watcher. Quando finalizado, retorna o thumbnail WebP em base64 para polling do sistema de produção.",
    params: [
      {
        name: "path",
        type: "string",
        required: true,
        location: "query",
        description: "Caminho lógico do arquivo.",
      },
      {
        name: "page",
        type: "integer",
        required: false,
        default: "1",
        location: "query",
        description: "Página do thumbnail, 1-indexed.",
      },
    ],
    responseExample: JSON.stringify(
      {
        path: "approvals/123456/WPC_Caramelo_Salgado_900g_20041254_R02_Editavel.pdf",
        asset_id: "0d3d7b2a-...",
        file_id: "c3d4e5f6-...",
        asset_version_id: "f8b9c0d1-...",
        status: "ready",
        ready: true,
        progress: 100,
        thumbnail: {
          content_type: "image/webp",
          base64: "UklGR...",
          bytes: 18432,
        },
        error: null,
      },
      null,
      2,
    ),
  },
  {
    id: "thumbnail",
    method: "GET",
    path: `${INTEGRATION_BASE}/thumbnail`,
    summary: "Miniatura da página 1, direto do PDF",
    description:
      "A miniatura da página 1 em WebP sem perdas, com 350 px no lado maior, pintada pelo render direto do PDF original: não espera preprepare nem render, e por isso serve à listagem logo depois do upload. Só PDF. O teto é 10 s, sempre — além dele a resposta é 504 (thumbnail_timeout), e a mesma arte vai responder 504 de novo; a espera por uma vaga tem o mesmo limite e responde 429 (thumbnail_busy) com Retry-After. A miniatura fica guardada: a segunda chamada não renderiza (X-LinkFlow-Thumbnail-Cache: hit) e continua respondendo depois que o original expira. O ETag muda com o arquivo, com a versão do render e com o acervo de fontes; mande-o em If-None-Match para receber 304 sem corpo. Os erros vêm em JSON com detail.code: 404 quando o path não tem arquivo no viewer; 410 expired quando o original expirou antes de a miniatura existir (reenvie pelo mesmo path); 415 thumbnail_pdf_only; 422 thumbnail_refused quando o render recusou o PDF, com o motivo em detail.reason; 502 thumbnail_failed; 503 thumbnail_source_unavailable, cli_unavailable ou font_environment_changed, com Retry-After — é passageiro, repita. A miniatura que o viewer gera no preprepare, em base64, continua em preprepare/status.",
    params: [
      {
        name: "path",
        type: "string",
        required: true,
        location: "query",
        description: "Caminho lógico do arquivo PDF (ex: 'approvals/123456/arquivo.pdf').",
      },
      {
        name: "If-None-Match",
        type: "string",
        required: false,
        location: "header",
        description: "O ETag de uma resposta anterior. Se a miniatura não mudou, a resposta é 304, sem corpo.",
      },
    ],
    responseExample: [
      "HTTP/1.1 200 OK",
      "Content-Type: image/webp",
      'ETag: "9c41e0b7a2..."',
      "Cache-Control: private, no-cache",
      "X-LinkFlow-Thumbnail-Cache: miss",
      "",
      "<bytes do WebP: página 1, 350 px no lado maior, sem perdas>",
      "",
      "# erro (ex.: 504)",
      JSON.stringify(
        {
          detail: {
            code: "thumbnail_timeout",
            message: "A miniatura passou do teto de 10 s.",
            timeout_seconds: 10,
          },
        },
        null,
        2,
      ),
    ].join("\n"),
    responseFile: "miniatura.webp",
  },
  {
    id: "start-render",
    method: "POST",
    path: `${INTEGRATION_BASE}/renders`,
    summary: "Iniciar render",
    description:
      "Inicia ou reutiliza o render homologado Viewer API v1 para o arquivo identificado por path. Com o path de uma pasta de separações do Esko, renderiza o conjunto (LEN) e, se a pasta tiver a subpasta view, renderiza também a VIEW: a resposta traz o bloco view, no mesmo formato do conjunto e com variant=view, ou view=null quando não há subpasta. A VIEW tem as mesmas defesas do conjunto (min_inks e a janela de quietude), mas elas não derrubam o LEN: se a VIEW ainda está chegando, o LEN renderiza e o bloco view diz status=separation_set_settling ou separation_set_incomplete, com o detalhe em error. É só repetir a chamada.",
    params: [
      {
        name: "path",
        type: "string",
        required: true,
        location: "body",
        description: "Caminho lógico do arquivo, ou da pasta de um job de separações do Esko (os TIFFs de uma chapa).",
      },
      {
        name: "min_inks",
        type: "integer",
        required: false,
        location: "body",
        description:
          "Só com path de pasta de separações: quantas tintas o app mandou. Com ele o conjunto monta quando a contagem bate, sem esperar a janela de quietude; abaixo dela a resposta é 409 com detail.code=separation_set_incomplete. Vale também para a subpasta view, sem derrubar o LEN. Ignorado para arquivo.",
      },
      {
        name: "text_analysis",
        type: "boolean",
        required: false,
        location: "body",
        default: "false",
        description:
          "Pede, junto com o render, a identificação de texto (Google Vision) com revisão ortográfica. Com o render já pronto a análise entra na fila agora (resposta text_analysis: started); com o render na fila ela roda sozinha quando ele ficar pronto (scheduled); sem preparo ainda, repita a chamada (pending_preprepare). Custo por chamada no Vision; o resultado fica no render e aparece no Modo Texto de qualquer link com text_mode: true.",
      },
    ],
    requestExample: JSON.stringify(
      { path: "approvals/123456/WPC_Caramelo_Salgado_900g_20041254_R02_Editavel.pdf", text_analysis: true },
      null,
      2,
    ),
    responseExample: JSON.stringify(
      {
        path: "approvals/123456/WPC_Caramelo_Salgado_900g_20041254_R02_Editavel.pdf",
        asset_id: "0d3d7b2a-...",
        file_id: "c3d4e5f6-...",
        render_id: "d7e8f9a0-...",
        job_id: "a1b2c3d4-...",
        status: "queued",
        ready: false,
        progress: 0,
        error: null,
        text_analysis: "scheduled",
      },
      null,
      2,
    ),
  },
  {
    id: "render-status",
    method: "GET",
    path: `${INTEGRATION_BASE}/renders/status`,
    summary: "Status do render",
    description:
      "Retorna o estado atual do render homologado mais recente, incluindo porcentagem de 0 a 100. status=expired (com ready=false, progress=0 e expired_at) quando o arquivo expirou no viewer: reenvie pelo mesmo path. POST renders de um arquivo expirado responde 409 com detail.code=expired, e o download do original, 410 com o mesmo code. Com path de pasta de separações, traz também o bloco view, o estado do render da subpasta view (status=not_started enquanto ela não foi pedida), sem enfileirar nada.",
    params: [
      {
        name: "path",
        type: "string",
        required: true,
        location: "query",
        description: "Caminho do asset no File Manager, ou da pasta de um job de separações do Esko.",
      },
    ],
    responseExample: JSON.stringify(
      {
        path: "approvals/123456/WPC_Caramelo_Salgado_900g_20041254_R02_Editavel.pdf",
        asset_id: "0d3d7b2a-...",
        file_id: "c3d4e5f6-...",
        render_id: "d7e8f9a0-...",
        job_id: "a1b2c3d4-...",
        status: "running",
        ready: false,
        progress: 42,
        error: null,
      },
      null,
      2,
    ),
  },
  {
    id: "notes",
    method: "GET",
    path: `${INTEGRATION_BASE}/notes`,
    summary: "Anotações do arquivo, com o recorte da arte",
    description:
      "As anotações do arquivo como a lista do relatório em PDF as mostra: autor, data, comentário e o recorte da arte em volta do lugar anotado, com a marca do ponto, em JPEG base64. number é o número do pino no PDF (página, depois ordem de criação), e a lista vem nessa ordem. Marca-texto fica de fora. Os comentários saem sempre; crops_status diz dos recortes: ready (lidos; crop null só onde a página não tem medida física), preparing (a arte está a caminho — peça de novo em instantes), unavailable (sem render pronto nem a caminho) ou expired (o arquivo expirou: reenvie pelo mesmo path). Com path de pasta de separações, as notas são as do conjunto.",
    params: [
      {
        name: "path",
        type: "string",
        required: true,
        location: "query",
        description: "Caminho do asset no File Manager, ou da pasta de um job de separações do Esko.",
      },
    ],
    responseExample: JSON.stringify(
      {
        path: "approvals/123456/WPC_Caramelo_Salgado_900g_20041254_R02_Editavel.pdf",
        asset_id: "0d3d7b2a-...",
        file_id: "c3d4e5f6-...",
        crops_status: "ready",
        notes: [
          {
            id: "7b1f4c2e-...",
            number: 1,
            page: 1,
            author: "Maria Silva",
            author_email: "maria@cliente.com.br",
            created_at: "2026-10-29T14:32:10.512000+00:00",
            updated_at: "2026-10-29T14:32:10.512000+00:00",
            body: "Trocar o Pantone do logotipo",
            is_completed: false,
            completed_by: null,
            completed_at: null,
            variant: null,
            parent_note_id: null,
            x_mm: 112.4,
            y_mm: 48.9,
            w_mm: null,
            h_mm: null,
            crop: {
              content_type: "image/jpeg",
              base64: "/9j/4AAQSkZJRgABAQAAAQABAAD...",
              bytes: 48213,
              width_px: 640,
              height_px: 400,
              window_mm: { left: 73.4, top: 24.525, right: 151.4, bottom: 73.275 },
              target_px: { x: 320.0, y: 200.0 },
            },
          },
        ],
      },
      null,
      2,
    ),
  },
  {
    id: "plans",
    method: "GET",
    path: `${INTEGRATION_BASE}/plans`,
    summary: "Tabela de planos",
    description:
      "O menor plano em que cada chave de tools existe — a mesma tabela que o servidor usa para travar e a tela usa para desenhar o selo. plans vem em ordem (cada plano inclui o anterior); por chave, o plano mínimo, se é opt-in (exige true explícito mesmo no plano que a inclui) e em que tipo de link vale. Leia daqui em vez de copiar: a tabela pode mudar, e os links vivos seguem a tabela do momento.",
    params: [],
    responseExample: JSON.stringify(
      {
        plans: ["starter", "pro", "enterprise"],
        default_plan: "starter",
        tools: {
          barcode: { min_plan: "starter", opt_in: false, applies_to: ["viewer", "compare"] },
          barcode_auto: { min_plan: "pro", opt_in: false, applies_to: ["viewer"] },
          braille: { min_plan: "pro", opt_in: false, applies_to: ["viewer", "compare"] },
          text_mode: { min_plan: "enterprise", opt_in: true, applies_to: ["viewer"] },
          "…": "uma entrada por chave de tools",
        },
      },
      null,
      2,
    ),
  },
  {
    id: "public-view",
    method: "POST",
    path: `${INTEGRATION_BASE}/public-views`,
    summary: "Gerar view pública",
    description:
      "Cria uma URL embedável em iframe, válida por 1 hora, com controle do que o convidado pode fazer. Ver e escrever comentários são duas chaves separadas (tools.notes e tools.notes_create). Com o path de uma pasta de separações do Esko, a view nasce na peça inteira, com o seletor LEN | VIEW, e os renders do LEN e da subpasta view são garantidos na emissão; a resposta traz path (a pasta), kind=separation_set, anchor_path e o bloco view do POST renders. Com o path de um arquivo, nada muda.",
    params: [
      {
        name: "path",
        type: "string",
        required: true,
        location: "body",
        description: "Caminho lógico do arquivo, ou da pasta de um job de separações do Esko (os TIFFs de uma chapa).",
      },
      {
        name: "min_inks",
        type: "integer",
        required: false,
        location: "body",
        description:
          "Só com path de pasta de separações: quantas tintas o app mandou. Com ele o conjunto monta quando a contagem bate, sem esperar a janela de quietude; abaixo dela a resposta é 409 com detail.code=separation_set_incomplete. Vale também para a subpasta view, sem derrubar o LEN. Ignorado para arquivo.",
      },
      {
        name: "tools",
        type: "object",
        required: false,
        location: "body",
        description:
          "O que o convidado pode fazer com a arte. Cada chave é um booleano e todas são opcionais: o que você não enviar fica no padrão, e um link já emitido nunca muda de comportamento porque uma chave nova nasceu. Detalhe de cada uma na tabela «Chaves de tools», abaixo. A chave page_info saiu em 13/09/2026: a view pública não mostra o painel «Metadados do arquivo», e enviá-la não dá erro nem efeito.",
        fields: TOOLS_FIELDS_VIEW,
      },
      {
        name: "plan",
        type: "string",
        required: false,
        location: "body",
        default: "starter",
        description:
          "starter, pro ou enterprise: o plano do cliente, e o TETO do que o convidado vê. A chave acima do plano aparece travada, opaca, com o selo do plano em que existe, e a rota responde 403. tools continua valendo por cima e só subtrai: false esconde sem selo; true não sobe acima do plano. Ausente, o link nasce starter — o plano de entrada é o padrão; só links anteriores a 13/09, sem plano gravado, ficam sem teto. A coluna «Plano» da tabela de chaves diz o mínimo de cada uma; GET /integration/v1/plans é a fonte viva.",
      },
      {
        name: "info",
        type: "object",
        required: false,
        location: "body",
        description:
          "A ficha do trabalho, mostrada no painel «Informações» à esquerda do viewer. É uma cópia tirada na emissão: editar no LinkFlow depois não muda o link — gere outro. Ausente, ou com os quatro campos vazios, o painel não abre (a menos que tools.download esteja ligada, e aí ele existe só pelo botão de baixar).",
        fields: INFO_FIELDS,
      },
      {
        name: "X-LinkFlow-Requester-Name",
        type: "string",
        required: false,
        location: "header",
        description: "Nome da pessoa que abrirá a view. Usado como autor de notas.",
      },
      {
        name: "X-LinkFlow-Requester-Email",
        type: "string",
        required: false,
        location: "header",
        description: "Email da pessoa que abrirá a view. Usado como email do autor de notas.",
      },
      {
        name: "X-LinkFlow-Organization-Id",
        type: "string",
        required: false,
        location: "header",
        description:
          "Código da empresa do cliente no LinkFlow (até 128 caracteres). É a quem o teste grátis do Viewer Pro é dado: num link starter, o botão «Viewer Pro» pode oferecer o teste, e o clique libera o Pro em todos os links da empresa pelo prazo do teste — o plano é lido a cada pedido, e durante o teste o link vale pro. Sem ele, o link nunca oferece o teste. É também a chave do dicionário da empresa (a ortografia do link usa o global + o dela, e vale o dela quando os dois falam da mesma palavra), da tela «Uso dos recursos» e da coluna «Organização» da Auditoria. A resposta ecoa o valor gravado em organization_id.",
      },
      {
        name: "X-LinkFlow-Organization",
        type: "string",
        required: false,
        location: "header",
        description:
          "Nome da empresa (até 256 caracteres), mostrado no convite do teste, na tela «Testes do Pro» e onde o gerenciador lista empresas (Dicionário, Uso, Auditoria). O id é a chave; o nome é o último que chegou. Em UTF-8 ou latin-1.",
      },
    ],
    requestExample: JSON.stringify(
      {
        path: "approvals/123456/WPC_Caramelo_Salgado_900g_20041254_R02_Editavel.pdf",
        plan: "pro",
        tools: {
          channel_selector: true,
          eyedropper: true,
          coverage_filter: true,
          barcode: true,
          barcode_auto: true,
          braille: true,
          ocr: true,
          table_ocr: true,
          text_mode: false,
          notes: true,
          notes_create: true,
          download: true,
        },
        info: {
          description: "WPC Caramelo Salgado 900g — R02",
          client_code: "20041254",
          reference_code: "R02",
          observations_html: "<p>Conferir o <strong>Pantone 871 C</strong> na faca.</p>",
        },
      },
      null,
      2,
    ),
    responseExample: JSON.stringify(
      {
        plan: "pro",
        url: "https://viewer.seudominio.com/public-view/Rk2...",
        token: "Rk2...",
        expires_at: "2026-05-29T18:30:00Z",
        path: "approvals/123456/WPC_Caramelo_Salgado_900g_20041254_R02_Editavel.pdf",
        render_id: "d7e8f9a0-...",
      },
      null,
      2,
    ),
  },
  {
    id: "public-compare",
    method: "POST",
    path: `${INTEGRATION_BASE}/public-compares`,
    summary: "Gerar view pública de comparação",
    description:
      "O mesmo link da view pública, para um PAR de arquivos: abre a comparação em /public-compare/{token}, sem slug na URL, com o mesmo prazo, as mesmas ferramentas e a mesma autoria de notas. Os dois renders são garantidos na emissão; a página espera os dois ficarem prontos. Revoga-se pelo mesmo DELETE /public-views/{id}. O par pode ser de duas artes (a comparação gráfica), de dois documentos (o diff de texto) ou de um documento com uma arte — a conferência de dizeres, do plano Pro em diante (tools.document_conference); fora dele esse par responde 422. Uma diferença de contrato: este endpoint NÃO aceita info — a ficha do trabalho é do link unitário, porque a tela de comparação não tem o painel «Informações» onde ela seria mostrada. Enviar info aqui não dá erro: o campo é ignorado.",
    params: [
      {
        name: "path_a",
        type: "string",
        required: true,
        location: "body",
        description: "Caminho lógico do arquivo do lado A (a referência).",
      },
      {
        name: "path_b",
        type: "string",
        required: true,
        location: "body",
        description: "Caminho lógico do arquivo do lado B. Tem de ser diferente do A.",
      },
      {
        name: "page_a",
        type: "integer",
        required: false,
        location: "body",
        default: "1",
        description: "Página do lado A em que a comparação abre (1-based). Quem recebe o link pode trocar na tela.",
      },
      {
        name: "page_b",
        type: "integer",
        required: false,
        location: "body",
        default: "1",
        description: "Página do lado B em que a comparação abre (1-based).",
      },
      {
        name: "tools",
        type: "object",
        required: false,
        location: "body",
        description:
          "As mesmas chaves da view pública, valendo para os dois lados do par — mas nem todas têm o que fazer aqui, porque a tela de comparação não é a do viewer: ela não tem Painel Técnico nem painel «Informações». A tabela «Chaves de tools», abaixo, diz uma a uma o que acontece no par. Duas chaves só existem aqui: alignment_auto e differences. E «Exportar visualização» não aparece em link de comparação nenhum — exportar exige conta, e quem recebeu o link não tem uma.",
        fields: TOOLS_FIELDS_COMPARE,
      },
      {
        name: "plan",
        type: "string",
        required: false,
        location: "body",
        default: "starter",
        description:
          "starter, pro ou enterprise: o plano do cliente, e o TETO do que o convidado vê. A chave acima do plano aparece travada, opaca, com o selo do plano em que existe, e a rota responde 403. tools continua valendo por cima e só subtrai: false esconde sem selo; true não sobe acima do plano. Ausente, o link nasce starter — o plano de entrada é o padrão; só links anteriores a 13/09, sem plano gravado, ficam sem teto. A coluna «Plano» da tabela de chaves diz o mínimo de cada uma; GET /integration/v1/plans é a fonte viva.",
      },
      {
        name: "profile",
        type: "string",
        required: false,
        location: "body",
        description:
          "Nome do perfil de comparação com que o link abre (até 40 caracteres): «default» ou um dos perfis de teste configurados em Configurações › Comparação. Quem recebe o link não escolhe perfil — a escolha é de quem emite. Ausente (ou «default»), o link usa o perfil configurado para o plano dele e, sem um, o perfil padrão; nada é gravado. Nome desconhecido responde 422 com code perfil_desconhecido, antes de garantir render. Se o perfil for apagado depois da emissão, o link continua abrindo, no perfil do plano ou no padrão. A sessão da comparação devolve profile (os valores aplicados) e profile_origin: link, plan ou default.",
      },
      {
        name: "analysis_resolution",
        type: "string",
        required: false,
        location: "body",
        default: "normal",
        description:
          "Resolução da comparação: «normal» ou «high». «normal» (o padrão, quando ausente) compara a arte a até 400 dpi — e a página acima de 100 milhões de pixels cai para a metade da resolução do arquivo (ex.: 200 dpi numa caixa de 1,35 m renderizada a 400) — e é a mais rápida; «high» compara na resolução máxima dos dois arquivos, pixel a pixel — para traços finos e trapping —, e demora mais para mostrar as diferenças. Como o perfil, é escolha de quem emite: quem recebe o link não troca. Vale no par de embalagem (imagem ou PDF pelo composto); na comparação de documentos e com «Comparar por separação de cores» marcada não muda nada. Outro valor responde 422.",
      },
      {
        name: "ttl_hours",
        type: "number",
        required: false,
        location: "body",
        default: "1",
        description: "Prazo do link em horas; acima do teto de 24 é aparado para 24.",
      },
      {
        name: "X-LinkFlow-Requester-Name",
        type: "string",
        required: false,
        location: "header",
        description: "Nome da pessoa que abrirá a comparação. Usado como autor das notas dos dois lados.",
      },
      {
        name: "X-LinkFlow-Requester-Email",
        type: "string",
        required: false,
        location: "header",
        description: "Email da pessoa que abrirá a comparação. Usado como email do autor das notas.",
      },
      {
        name: "X-LinkFlow-Organization-Id",
        type: "string",
        required: false,
        location: "header",
        description:
          "Código da empresa do cliente no LinkFlow, como no link de um arquivo: num par starter, o teste grátis do Viewer Pro da empresa vale aqui também, e a ortografia do par usa o dicionário da empresa.",
      },
      {
        name: "X-LinkFlow-Organization",
        type: "string",
        required: false,
        location: "header",
        description: "Nome da empresa, mostrado no convite do teste, na tela «Testes do Pro» e onde o gerenciador lista empresas.",
      },
    ],
    requestExample: JSON.stringify(
      {
        path_a: "approvals/123456/WPC_Caramelo_Salgado_900g_R02.pdf",
        path_b: "approvals/123456/WPC_Caramelo_Salgado_900g_R03.pdf",
        page_a: 1,
        page_b: 1,
        plan: "starter",
        tools: {
          notes: true,
          notes_create: true,
          barcode: true,
          barcode_auto: true,
          braille: true,
          ocr: true,
          alignment_auto: false,
          differences: false,
        },
        ttl_hours: 2,
      },
      null,
      2,
    ),
    responseExample: JSON.stringify(
      {
        id: "7b1f4c2e-...",
        kind: "compare",
        plan: "starter",
        url: "https://viewer.seudominio.com/public-compare/Rk2...",
        token: "Rk2...",
        expires_at: "2026-09-04T18:30:00Z",
        path_a: "approvals/123456/WPC_Caramelo_Salgado_900g_R02.pdf",
        path_b: "approvals/123456/WPC_Caramelo_Salgado_900g_R03.pdf",
        render_a_id: "d7e8f9a0-...",
        render_b_id: "e8f9a0b1-...",
        page_a: 1,
        page_b: 1,
        profile: null,
        analysis_resolution: "normal",
      },
      null,
      2,
    ),
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const METHOD_COLORS: Record<string, string> = {
  GET: "bg-emerald-600/20 text-emerald-300 border-emerald-700",
  POST: "bg-blue-600/20 text-blue-300 border-blue-700",
  DELETE: "bg-red-600/20 text-red-300 border-red-700",
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="ml-2 rounded border border-ink-700 bg-ink-800 px-2 py-0.5 text-xs text-ink-300 hover:bg-ink-700 hover:text-ink-100 transition"
    >
      {copied ? "Copiado!" : "Copiar"}
    </button>
  );
}

function CodeBlock({ code, language = "bash" }: { code: string; language?: string }) {
  void language;
  return (
    <div className="relative mt-2 rounded border border-ink-700 bg-ink-950 p-3">
      <div className="absolute right-2 top-2">
        <CopyButton text={code} />
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap break-all pr-16 text-xs text-ink-200 font-mono leading-relaxed">
        {code}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Try It panel
// ---------------------------------------------------------------------------

interface TryItPanelProps {
  endpoint: Endpoint;
  bearerToken: string | null;
}

function TryItPanel({ endpoint, bearerToken }: TryItPanelProps) {
  const [apiKey, setApiKey] = useState("");
  const [params, setParams] = useState<Record<string, string>>({});
  const [body, setBody] = useState(endpoint.requestExample ?? "");
  const [response, setResponse] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Solta o blob da imagem anterior ao trocar de resposta e ao fechar o painel.
  useEffect(
    () => () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    },
    [imageUrl],
  );

  const effectiveToken = apiKey.trim() || bearerToken;

  const handleRun = useCallback(async () => {
    setLoading(true);
    setResponse(null);
    setStatusCode(null);
    setImageUrl(null);

    let url = endpoint.path;

    // Replace path params
    for (const [k, v] of Object.entries(params)) {
      url = url.replace(`{${k}}`, encodeURIComponent(v));
    }

    // Append query params
    const queryParams = endpoint.params.filter((p) => p.location === "query");
    const qs = new URLSearchParams();
    for (const p of queryParams) {
      const val = params[p.name];
      if (val) qs.set(p.name, val);
    }
    if (qs.toString()) url += `?${qs.toString()}`;

    const headers: Record<string, string> = {};
    if (apiKey.trim()) {
      headers["X-API-Key"] = apiKey.trim();
    } else if (bearerToken) {
      headers["Authorization"] = `Bearer ${bearerToken}`;
    }
    for (const p of endpoint.params.filter((item) => item.location === "header")) {
      const val = params[p.name];
      if (val) headers[p.name] = val;
    }
    if (endpoint.method !== "GET" && endpoint.method !== "DELETE") {
      headers["Content-Type"] = "application/json";
    }

    try {
      const init: RequestInit = { method: endpoint.method, headers };
      if (body && endpoint.requestExample !== undefined) {
        init.body = body;
      }

      const res = await fetch(url, init);
      setStatusCode(res.status);

      // A miniatura é binária: lida como texto, viraria lixo na tela. Mostra a
      // imagem e, no lugar do corpo, os cabeçalhos que dizem o que ela é.
      const contentType = res.headers.get("content-type") ?? "";
      if (contentType.startsWith("image/")) {
        const blob = await res.blob();
        setImageUrl(URL.createObjectURL(blob));
        const linhas = [`Content-Type: ${contentType}`, `Tamanho: ${blob.size} bytes`];
        for (const nome of ["ETag", "X-LinkFlow-Thumbnail-Cache"]) {
          const valor = res.headers.get(nome);
          if (valor) linhas.push(`${nome}: ${valor}`);
        }
        setResponse(linhas.join("\n"));
        return;
      }

      const text = await res.text();
      try {
        setResponse(JSON.stringify(JSON.parse(text), null, 2));
      } catch {
        setResponse(text);
      }
    } catch (err) {
      setResponse(String(err));
    } finally {
      setLoading(false);
    }
  }, [endpoint, params, body, apiKey, bearerToken]);

  const bodyParams = endpoint.params.filter((p) => p.location === "body");
  const inputParams = endpoint.params.filter((p) => p.location !== "body");

  return (
    <div className="mt-4 rounded-lg border border-ink-700 bg-ink-850 p-4">
      <h4 className="text-xs font-semibold uppercase tracking-widest text-ink-400 mb-3">
        Testar endpoint
      </h4>

      {/* Auth field */}
      <div className="mb-3">
        <label className="block text-xs text-ink-400 mb-1">
          API Key <span className="text-ink-500">(opcional — usa sessão atual se vazia)</span>
        </label>
        <input
          type="text"
          placeholder="lfk_..."
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          className="w-full rounded border border-ink-700 bg-ink-900 px-3 py-1.5 text-xs text-ink-100 placeholder-ink-500 focus:border-emerald-600 focus:outline-none font-mono"
        />
      </div>

      {/* Input params (query / path) */}
      {inputParams.map((p) => (
        <div key={p.name} className="mb-3">
          <label className="block text-xs text-ink-400 mb-1">
            {p.name}
            {p.required && <span className="ml-1 text-red-400">*</span>}
            <span className="ml-1 text-ink-500">({p.location})</span>
          </label>
          <input
            type="text"
            placeholder={p.default ?? p.description.slice(0, 40)}
            value={params[p.name] ?? ""}
            onChange={(e) => setParams((prev) => ({ ...prev, [p.name]: e.target.value }))}
            className="w-full rounded border border-ink-700 bg-ink-900 px-3 py-1.5 text-xs text-ink-100 placeholder-ink-500 focus:border-emerald-600 focus:outline-none font-mono"
          />
        </div>
      ))}

      {/* Body textarea */}
      {bodyParams.length > 0 && (
        <div className="mb-3">
          <label className="block text-xs text-ink-400 mb-1">
            Body <span className="text-ink-500">(JSON)</span>
          </label>
          <textarea
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full rounded border border-ink-700 bg-ink-900 px-3 py-1.5 text-xs text-ink-100 focus:border-emerald-600 focus:outline-none font-mono resize-y"
          />
        </div>
      )}

      <button
        type="button"
        disabled={loading || !effectiveToken}
        onClick={() => void handleRun()}
        className="rounded border border-emerald-700 bg-emerald-600/20 px-4 py-1.5 text-xs font-medium text-emerald-300 hover:bg-emerald-600/30 transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {loading ? "Aguardando..." : "Enviar"}
      </button>
      {!effectiveToken && (
        <span className="ml-3 text-xs text-ink-500">Informe uma API key ou faça login.</span>
      )}

      {/* Response */}
      {response !== null && (
        <div className="mt-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-ink-400">Resposta</span>
            {statusCode !== null && (
              <span
                className={[
                  "rounded px-1.5 py-0.5 text-xs font-mono font-medium",
                  statusCode < 300
                    ? "bg-emerald-600/20 text-emerald-300"
                    : statusCode < 400
                      ? "bg-yellow-600/20 text-yellow-300"
                      : "bg-red-600/20 text-red-300",
                ].join(" ")}
              >
                {statusCode}
              </span>
            )}
          </div>
          <CodeBlock code={response} language="json" />
          {imageUrl && (
            <img src={imageUrl} alt="Imagem da resposta" className="mt-2 max-h-96 rounded border border-ink-700" />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Endpoint card
// ---------------------------------------------------------------------------

interface EndpointCardProps {
  endpoint: Endpoint;
  bearerToken: string | null;
}

function buildCurl(endpoint: Endpoint, bearerToken: string | null): string {
  const authFlag = bearerToken
    ? `-H "Authorization: Bearer ${bearerToken.slice(0, 12)}..."`
    : `-H "X-API-Key: lfk_SUA_CHAVE"`;

  let url = `http://seudominio.com${endpoint.path}`;

  if (endpoint.method === "GET" && endpoint.params.some((p) => p.location === "query")) {
    const qs = endpoint.params
      .filter((p) => p.location === "query")
      .map((p) => `${p.name}=VALOR`)
      .join("&");
    url += `?${qs}`;
  }

  const headerFlags = endpoint.params
    .filter((p) => p.location === "header")
    .map((p) => `-H "${p.name}: VALOR"`)
    .join(" \\\n  ");
  const outputFlag = endpoint.responseFile ? ` \\\n  -o ${endpoint.responseFile}` : "";
  const extraHeaders = (headerFlags ? ` \\\n  ${headerFlags}` : "") + outputFlag;

  if (endpoint.requestExample) {
    return `curl -X ${endpoint.method} "${url}" \\\n  ${authFlag}${extraHeaders} \\\n  -H "Content-Type: application/json" \\\n  -d '${endpoint.requestExample.replace(/\n/g, " ")}'`;
  }

  return `curl -X ${endpoint.method} "${url}" \\\n  ${authFlag}${extraHeaders}`;
}

/** A cor de «Onde vale» — a diferença entre uma garantia e um botão escondido. */
const PLAN_ROTULO: Record<PlanName, string> = { starter: "Starter", pro: "Pro", enterprise: "Enterprise" };

const SCOPE_STYLE: Record<NonNullable<ParamField["scope"]>, string> = {
  "tela e rota": "text-emerald-300",
  "tela e payload": "text-emerald-300",
  "só tela": "text-amber-300",
  "só rota": "text-ink-400",
};

function FieldsTable({ param, fields }: { param: string; fields: ParamField[] }) {
  // A coluna só existe onde a pergunta existe: em `info` são quatro campos de
  // texto, e uma coluna inteira dizendo «só tela» quatro vezes é ruído.
  const comEscopo = fields.some((f) => f.scope);
  const comPlano = fields.some((f) => f.plan);
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-widest text-ink-400 mb-2">
        Chaves de <code className="font-mono normal-case tracking-normal text-ink-200">{param}</code>
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-ink-500">
              <th className="pb-1 pr-4 font-medium">Chave</th>
              <th className="pb-1 pr-4 font-medium">Tipo</th>
              <th className="pb-1 pr-4 font-medium">Padrão</th>
              {comEscopo ? <th className="pb-1 pr-4 font-medium">Onde vale</th> : null}
              {comPlano ? <th className="pb-1 pr-4 font-medium">Plano</th> : null}
              <th className="pb-1 font-medium">O que faz</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-800">
            {fields.map((f) => (
              <tr key={f.name}>
                <td className="py-1.5 pr-4 align-top font-mono text-ink-100 whitespace-nowrap">{f.name}</td>
                <td className="py-1.5 pr-4 align-top text-ink-400 whitespace-nowrap">{f.type}</td>
                <td className="py-1.5 pr-4 align-top font-mono text-ink-400">{f.default}</td>
                {comEscopo ? (
                  <td className={`py-1.5 pr-4 align-top whitespace-nowrap ${f.scope ? SCOPE_STYLE[f.scope] : ""}`}>
                    {f.scope ?? "—"}
                  </td>
                ) : null}
                {comPlano ? (
                  <td className="py-1.5 pr-4 align-top whitespace-nowrap text-ink-300">{f.plan ? PLAN_ROTULO[f.plan] : "—"}</td>
                ) : null}
                <td className="py-1.5 align-top text-ink-300">
                  {f.description}
                  {f.note ? <span className="mt-1 block text-ink-400 italic">{f.note}</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* A legenda é o ponto da coluna: sem ela, «só tela» parece um detalhe de
          implementação, quando é a diferença entre esconder e proibir. */}
      {comEscopo ? (
        <p className="mt-2 text-[11px] leading-snug text-ink-500">
          <span className="text-emerald-300">tela e rota</span> — o controle some e o endpoint por trás responde 403 a
          quem usar este token. <span className="text-amber-300">só tela</span> — o controle não é desenhado, mas a
          rota por trás não consulta a chave: serve para decidir o que o convidado vê, não para guardar segredo.{" "}
          <span className="text-ink-400">só rota</span> — não há controle na tela hoje; a chave governa apenas o
          endpoint. <span className="text-emerald-300">tela e payload</span> — não há endpoint a barrar: o dado é
          calculado no servidor e não sai no bootstrap do link, o que guarda tanto quanto um 403.
        </p>
      ) : null}
      {comPlano ? (
        <p className="mt-2 text-[11px] leading-snug text-ink-500">
          <span className="text-ink-300">Plano</span> — o menor plano em que a chave existe. Com <code className="font-mono">plan</code> no
          link, a chave acima do plano aparece travada, opaca, com o selo do plano em que existe, e a rota responde 403;
          <code className="font-mono"> false</code> explícito esconde sem selo, e <code className="font-mono">true</code> não sobe acima do plano.
          A fonte viva é <code className="font-mono">GET /integration/v1/plans</code>.
        </p>
      ) : null}
    </div>
  );
}

function EndpointCard({ endpoint, bearerToken }: EndpointCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [tryItOpen, setTryItOpen] = useState(false);
  const curlSnippet = buildCurl(endpoint, bearerToken);

  return (
    <div className="rounded-lg border border-ink-700 bg-ink-850 overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-ink-800 transition"
      >
        <span
          className={[
            "shrink-0 rounded border px-2 py-0.5 text-xs font-mono font-semibold",
            METHOD_COLORS[endpoint.method],
          ].join(" ")}
        >
          {endpoint.method}
        </span>
        <code className="flex-1 text-sm text-ink-100 font-mono">{endpoint.path}</code>
        <span className="text-sm text-ink-300 hidden sm:block">{endpoint.summary}</span>
        <svg
          viewBox="0 0 24 24"
          className={["h-4 w-4 shrink-0 text-ink-400 transition-transform", expanded ? "rotate-180" : ""].join(" ")}
          fill="none"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Body */}
      {expanded && (
        <div className="border-t border-ink-700 px-4 py-4 space-y-4">
          <p className="text-sm text-ink-300">{endpoint.description}</p>

          {/* Params table */}
          {endpoint.params.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-widest text-ink-400 mb-2">
                Parâmetros
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-ink-500">
                      <th className="pb-1 pr-4 font-medium">Nome</th>
                      <th className="pb-1 pr-4 font-medium">Tipo</th>
                      <th className="pb-1 pr-4 font-medium">Local</th>
                      <th className="pb-1 pr-4 font-medium">Req.</th>
                      <th className="pb-1 font-medium">Descrição</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-800">
                    {endpoint.params.map((p) => (
                      <tr key={p.name}>
                        <td className="py-1.5 pr-4 font-mono text-ink-100">{p.name}</td>
                        <td className="py-1.5 pr-4 text-ink-400">{p.type}</td>
                        <td className="py-1.5 pr-4 text-ink-500">{p.location}</td>
                        <td className="py-1.5 pr-4">
                          {p.required ? (
                            <span className="text-red-400">sim</span>
                          ) : (
                            <span className="text-ink-500">não{p.default ? ` (padrão: ${p.default})` : ""}</span>
                          )}
                        </td>
                        <td className="py-1.5 text-ink-300">{p.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* As chaves de dentro de `tools` e `info`, uma a uma. Fora da tabela
              de parâmetros de propósito: são quinze linhas, e enfiá-las numa
              célula de descrição é o que fazia ninguém as ler. */}
          {endpoint.params
            .filter((p) => p.fields && p.fields.length > 0)
            .map((p) => (
              <FieldsTable key={`${p.name}-fields`} param={p.name} fields={p.fields ?? []} />
            ))}

          {/* Curl */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-ink-400 mb-1">
              Exemplo curl
            </h4>
            <CodeBlock code={curlSnippet} language="bash" />
          </div>

          {/* Response example */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-ink-400 mb-1">
              Exemplo de resposta
            </h4>
            <CodeBlock code={endpoint.responseExample} language="json" />
          </div>

          {/* Try it */}
          <div>
            <button
              type="button"
              onClick={() => setTryItOpen((v) => !v)}
              className="rounded border border-ink-600 bg-ink-800 px-3 py-1.5 text-xs font-medium text-ink-200 hover:bg-ink-700 hover:text-ink-100 transition"
            >
              {tryItOpen ? "Fechar painel de teste" : "Testar agora"}
            </button>
            {tryItOpen && <TryItPanel endpoint={endpoint} bearerToken={bearerToken} />}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ApiDocs() {
  const accessToken = useAuthStore((s) => s.accessToken);

  const apiKeysEndpoints = ENDPOINTS.filter((e) => e.id.includes("api-key"));
  const integrationEndpoints = ENDPOINTS.filter((e) => !e.id.includes("api-key"));

  return (
    <section className="flex h-full min-h-0 flex-col overflow-auto px-6 py-6">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-ink-100">API Externa v1</h1>
        <p className="mt-2 max-w-3xl text-sm text-ink-300">
          Endpoints para integrar o app de produção ao Viewer usando o path lógico do arquivo.
          Todos aceitam autenticação via Bearer token (sessão) ou{" "}
          <code className="text-emerald-400">X-API-Key</code>.
        </p>
      </div>

      {/* Auth section */}
      <div className="mb-6 rounded-lg border border-ink-700 bg-ink-850 p-4">
        <h2 className="text-sm font-semibold text-ink-100 mb-2">Autenticação</h2>
        <p className="text-sm text-ink-300 mb-3">
          Toda requisição à API externa deve incluir um dos seguintes headers:
        </p>
        <div className="space-y-2">
          <div className="rounded border border-ink-700 bg-ink-900 p-3">
            <p className="text-xs font-semibold text-ink-300 mb-1">Opção 1 — API key (recomendado para integrações)</p>
            <code className="text-xs text-emerald-300 font-mono">X-API-Key: lfk_sua_chave_aqui</code>
          </div>
          <div className="rounded border border-ink-700 bg-ink-900 p-3">
            <p className="text-xs font-semibold text-ink-300 mb-1">Opção 2 — Token de sessão</p>
            <code className="text-xs text-blue-300 font-mono">Authorization: Bearer seu_access_token</code>
          </div>
        </div>
        <p className="mt-3 text-xs text-ink-400">
          Crie e gerencie suas API keys nos endpoints abaixo ou em <strong>Usuários → Chaves de API</strong> no painel
          admin. As chaves têm o prefixo <code className="text-emerald-400">lfk_</code> e o segredo é exibido apenas
          na criação.
        </p>
        <p className="mt-2 text-xs text-ink-400">
          Para views públicas com notas, envie também{" "}
          <code className="text-ink-200">X-LinkFlow-Requester-Name</code> e{" "}
          <code className="text-ink-200">X-LinkFlow-Requester-Email</code>. Para o teste grátis do Viewer Pro, o
          dicionário da empresa e o uso por empresa, a empresa do cliente:{" "}
          <code className="text-ink-200">X-LinkFlow-Organization-Id</code> e{" "}
          <code className="text-ink-200">X-LinkFlow-Organization</code>.
        </p>
      </div>

      {/* API keys section */}
      <div className="mb-6">
        <h2 className="mb-3 text-base font-semibold text-ink-100">Gerenciamento de API Keys</h2>
        <div className="space-y-2">
          {apiKeysEndpoints.map((e) => (
            <EndpointCard key={e.id} endpoint={e} bearerToken={accessToken} />
          ))}
        </div>
      </div>

      {/* Integration section */}
      <div className="mb-6">
        <h2 className="mb-3 text-base font-semibold text-ink-100">Integração por Path</h2>
        <div className="space-y-2">
          {integrationEndpoints.map((e) => (
            <EndpointCard key={e.id} endpoint={e} bearerToken={accessToken} />
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="rounded-lg border border-ink-700 bg-ink-850 p-4 text-xs text-ink-400 space-y-1">
        <p>
          <strong className="text-ink-300">Base URL:</strong>{" "}
          <code className="font-mono text-ink-200">/api/integration/v1</code>
        </p>
        <p>
          <strong className="text-ink-300">Swagger completo:</strong>{" "}
          <a
            href="/api/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 hover:underline"
          >
            /api/docs
          </a>{" "}
          (todos os endpoints internos e externos)
        </p>
        <p>
          <strong className="text-ink-300">OpenAPI JSON:</strong>{" "}
          <a
            href="/api/openapi.json"
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 hover:underline"
          >
            /api/openapi.json
          </a>
        </p>
        <p>
          <strong className="text-ink-300">Polling:</strong> consulte preprepare/render a cada ~10s pelo path.
          Quando o preprepare estiver pronto, o thumbnail vem em <code>thumbnail.base64</code>. Para a miniatura logo
          depois do upload, sem esperar o preprepare, use <code>GET thumbnail</code>: responde em até 10 s.
        </p>
        <p>
          <strong className="text-ink-300">Views públicas:</strong> a URL <code>/public-view/&lt;token&gt;</code> expira
          em 1 hora. Ver os comentários é <code>tools.notes</code>; criar e responder é <code>tools.notes_create</code> —
          duas chaves, porque há link que mostra a arte e não mostra a conversa interna sobre ela.
        </p>
        <p>
          <strong className="text-ink-300">Comparação pública:</strong> <code>public-compares</code> devolve
          <code> /public-compare/&lt;token&gt;</code>, que abre os dois arquivos lado a lado sem slug na URL, com o
          mesmo prazo e as mesmas ferramentas. As notas são por lado (<code>asset_id</code>), e a revogação é o
          mesmo <code>DELETE /public-views/&#123;id&#125;</code>. O perfil de comparação sai, nesta ordem, do
          <code> profile</code> do link, do perfil configurado para o plano e do padrão; a sessão devolve
          <code> profile</code> e <code>profile_origin</code> (<code>link</code>, <code>plan</code> ou
          <code> default</code>). A lista de diferenças do link usa as réguas de granulometria desse perfil (a
          página manda <code>public_view=&lt;token&gt;</code> em <code>difference-regions</code>; a resposta declara
          <code> profile_origin</code>).
        </p>
      </div>
    </section>
  );
}
